import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Headphones, Send, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, PageHeader, Input, Button } from "@/components/ui-kit";

const CS_SUGGESTIONS = [
  "Tanya ketersediaan stok material",
  "Tanya harga & penawaran",
  "Cek status pesanan saya",
  "Komplain kualitas material",
  "Tanya jadwal pengiriman",
];

function formatTs(ts) {
  if (!ts) return "";
  try { return new Date(ts).toLocaleTimeString("id-ID", { timeZone: "Asia/Jakarta", hour: "2-digit", minute: "2-digit", hour12: false }); } catch { return ""; }
}
function formatDateTs(ts) {
  if (!ts) return "";
  try { return new Date(ts).toLocaleDateString("id-ID", { timeZone: "Asia/Jakarta", day: "2-digit", month: "short" }); } catch { return ""; }
}

/**
 * ClientChatCs — full-page CS chat for the client portal.
 * Iter v24 #5 — paired with the floating popout (same backend endpoints, fully synced).
 */
export default function ClientChatCs() {
  const { user } = useAuth();
  const [threadId, setThreadId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [showChips, setShowChips] = useState(false);
  const endRef = useRef(null);

  const ensureThread = useCallback(async () => {
    try {
      const r = await api.post("/chat/cs/ensure-thread");
      setThreadId(r.data.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memuat chat"); }
  }, []);

  const loadMessages = useCallback(async (tid) => {
    if (!tid) return;
    try {
      const r = await api.get(`/chat/threads/${tid}/messages`);
      setMessages(r.data);
      await api.post(`/chat/threads/${tid}/read`);
    } catch { /* */ }
  }, []);

  useEffect(() => { ensureThread(); }, [ensureThread]);
  useEffect(() => {
    if (!threadId) return;
    loadMessages(threadId);
    const id = setInterval(() => loadMessages(threadId), 5000);
    return () => clearInterval(id);
  }, [threadId, loadMessages]);
  useEffect(() => { if (endRef.current) endRef.current.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const text = body.trim();
    if (!text || sending || !threadId) return;
    setSending(true);
    try {
      await api.post(`/chat/threads/${threadId}/messages`, { body: text });
      setBody("");
      await loadMessages(threadId);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal kirim"); }
    finally { setSending(false); }
  };

  const grouped = useMemo(() => {
    const out = []; let last = "";
    for (const m of messages) {
      const d = formatDateTs(m.ts);
      if (d !== last) { out.push({ type: "date", key: "d-" + d, date: d }); last = d; }
      out.push({ type: "msg", key: m.id, msg: m });
    }
    return out;
  }, [messages]);

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 lg:p-8 space-y-5" data-testid="client-chat-cs-page">
      <PageHeader
        overline="Portal Klien"
        title="Chat Customer Service"
        description="Tanyakan apapun tentang pesanan, stok, atau penawaran Anda. AI menjawab instan, tim CS akan mengambil alih saat tersedia."
      />
      <Card className="p-0 overflow-hidden">
        <div className="flex flex-col h-[640px]">
          <div className="px-4 py-3 border-b border-stone-200 bg-gradient-to-r from-emerald-50 to-teal-50 flex items-center gap-2">
            <Headphones className="h-4 w-4 text-emerald-700" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-stone-900 truncate">Chat dengan Tim Kami</p>
              <p className="text-[10px] uppercase tracking-widest text-emerald-700">AI menjawab cepat · staff bisa ambil alih</p>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-3 bg-stone-50/30 space-y-1.5" data-testid="client-chat-cs-messages">
            {grouped.length === 0 && (
              <div className="text-center text-xs text-stone-400 py-10 px-4">
                <p className="text-stone-500">Halo {user?.name?.split(" ")[0] || "Klien"}! Tulis pesan untuk memulai percakapan dengan tim Customer Service. Klik tombol <Sparkles className="h-3 w-3 inline mb-0.5" /> di bawah untuk saran pertanyaan.</p>
              </div>
            )}
            {grouped.map((g) => {
              if (g.type === "date") {
                return (
                  <div key={g.key} className="text-center my-2">
                    <span className="text-[10px] uppercase tracking-widest text-stone-400 bg-stone-100 px-2 py-0.5 rounded-full">{g.date}</span>
                  </div>
                );
              }
              const m = g.msg;
              const mine = m.sender_id === user?.id;
              return (
                <div key={g.key} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[75%] px-3 py-2 rounded-lg text-sm ${mine ? "bg-emerald-600 text-white" : "bg-white border border-stone-200 text-stone-900"} ${m.deleted ? "italic opacity-50" : ""}`}>
                    {!mine && !m.deleted && (
                      <div className="text-[10px] uppercase tracking-wider font-semibold text-emerald-700 mb-0.5">{m.sender_name}</div>
                    )}
                    {m.deleted ? <span className="text-xs">(pesan dihapus)</span> : <div className="whitespace-pre-wrap break-words">{m.body}</div>}
                    <div className={`text-[10px] font-mono mt-1 ${mine ? "text-white/70 text-right" : "text-stone-400"}`}>{formatTs(m.ts)}</div>
                  </div>
                </div>
              );
            })}
            <div ref={endRef} />
          </div>

          <form onSubmit={submit} className="border-t border-stone-200 p-3 bg-white">
            {showChips && (
              <div className="flex flex-wrap gap-1 mb-2 pb-2 border-b border-stone-100" data-testid="client-chat-cs-chips">
                {CS_SUGGESTIONS.map((q, i) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => { setBody(q); setShowChips(false); }}
                    className="text-[11px] bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-200 px-2.5 py-1 rounded-full transition-colors"
                    data-testid={`client-chat-cs-chip-${i}`}
                  >{q}</button>
                ))}
              </div>
            )}
            <div className="flex items-end gap-2">
              <button
                type="button"
                onClick={() => setShowChips((v) => !v)}
                className={`shrink-0 p-2 transition-colors ${showChips ? "bg-emerald-600 text-white" : "bg-stone-100 hover:bg-emerald-100 text-stone-600 hover:text-emerald-700"}`}
                title="Saran pertanyaan"
                data-testid="client-chat-cs-chips-toggle"
              >
                <Sparkles className="h-3.5 w-3.5" />
              </button>
              <Input
                value={body}
                onChange={(e) => setBody(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(e); } }}
                placeholder="Tulis pesan untuk CS…"
                className="flex-1 !text-sm"
                data-testid="client-chat-cs-input"
              />
              <Button type="submit" disabled={sending || !body.trim()} className="!bg-emerald-600 hover:!bg-emerald-700" data-testid="client-chat-cs-send">
                <Send className="h-3.5 w-3.5" /> Kirim
              </Button>
            </div>
          </form>
        </div>
      </Card>
    </div>
  );
}
